<!-- Hero секция - Organism -->
<template>
  <section class="hero-content-with-noise relative min-h-screen overflow-hidden hero-section-clip">

    <!-- Эллипсы для градиентного эффекта -->
    <div
      class="hero-gradient-ellipse hero-gradient-ellipse-left pointer-events-none absolute left-1/4 top-1/3 rounded-full bg-[#ff3f1a]"
      style="width: 48rem; height: 48rem; filter: blur(160px); transform: translate(-50%, -50%) translateZ(0); z-index: 0;">
    </div>
    <div
      class="hero-gradient-ellipse hero-gradient-ellipse-right pointer-events-none absolute right-1/4 top-2/3 rounded-full bg-[#97D6DF]"
      style="width: 48rem; height: 48rem; filter: blur(160px); transform: translate(50%, -50%) translateZ(0); z-index: 0;">
    </div>

    <!-- Noise overlay поверх градиента и эллипсов -->
    <div class="absolute inset-0 noise-texture"
      style="opacity: 0.7; mix-blend-mode: overlay; pointer-events: none; z-index: 1;"></div>

    <!-- Контейнер с flex для управления порядком -->
    <div class="hero-container relative z-20 min-h-screen flex flex-col">
      <!-- Основной контент -->
      <div class="hero-main-content">
        <!-- Заголовок и подзаголовок -->
        <div class="">
          <h1
            class="hero-title mb-6 text-[62px] leading-none font-medium text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-100 to-white">
            Оставь свой отпечаток<br>в музыкальной индустрии
          </h1>

          <p class="hero-subtitle">
            в одно касание
          </p>
        </div>

        <!-- Кнопки действий -->
        <div class="flex gap-[11px] justify-center items-center flex-wrap">
          <button
            class="btn-cta-secondary inline-flex items-center gap-[6px] px-[20px] py-[15px] leading-none hover:opacity-90 transition-opacity"
            @click="handleLearnMore">
            Узнать больше
            <div class="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" viewBox="0 0 11 10" fill="none">
                <path
                  d="M4.63393 0.772284L7.94942 4.08778H0.000109958L0.000109832 5.17982L7.94942 5.17982L4.63393 8.49531L5.40623 9.26762L10.04 4.6338L5.40623 -1.91303e-05L4.63393 0.772284Z"
                  fill="white" />
              </svg>
            </div>
          </button>

          <button
            class="btn-cta-orange inline-flex items-center gap-[6px] px-[20px] py-[15px] leading-none hover:opacity-90 transition-opacity"
            @click="handleStartCareer">
            Начать карьеру
            <div class="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10" viewBox="0 0 11 10" fill="none">
                <path
                  d="M4.63393 0.772284L7.94942 4.08778H0.000109958L0.000109832 5.17982L7.94942 5.17982L4.63393 8.49531L5.40623 9.26762L10.04 4.6338L5.40623 -1.91303e-05L4.63393 0.772284Z"
                  fill="white" />
              </svg>
            </div>
          </button>
        </div>
      </div>

      <!-- Большой логотип с Lottie анимацией - абсолютное позиционирование -->
      <div class="hero-logo-container">
        <Vue3Lottie v-if="logoAnimation" :animationData="logoAnimation" :height="'auto'" :width="'auto'" :loop="3"
          :autoPlay="true" :renderer="'svg'" :rendererSettings="{
            preserveAspectRatio: 'xMidYMid slice',
            progressiveLoad: true,
            hideOnTransparent: true
          }" class="lottie-logo opacity-80" />
        <div v-else class="loading-text">Loading animation...</div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Vue3Lottie } from 'vue3-lottie'

// Загружаем Lottie анимацию
const logoAnimation = ref<any>(null)

// Загружаем JSON асинхронно
onMounted(async () => {
  try {
    const response = await fetch('/images/tlog.json')
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const data = await response.json()
    logoAnimation.value = data
    console.log('Lottie animation loaded:', data)
  } catch (error) {
    console.error('Failed to load Lottie animation:', error)
  }
})

const emit = defineEmits<{
  learnMore: []
  startCareer: []
}>()

const handleLearnMore = () => {
  emit('learnMore')
}

const handleStartCareer = () => {
  emit('startCareer')
}
</script>

<style scoped>
/* Градиентные эллипсы */
.hero-gradient-ellipse {
  opacity: 0.2;
  /* Еще более тусклые для мобильных (уменьшено на 30%) */
  transition: opacity 0.3s ease;
  will-change: opacity;
  /* GPU ускорение только для анимируемого свойства */
}

/* Увеличиваем прозрачность на десктопе */
@media (min-width: 1024px) {
  .hero-gradient-ellipse {
    opacity: 0.3;
  }
}

/* Заголовок с кастомным шрифтом */
.hero-title {
  font-family: 'Halvar Breitschrift', sans-serif;
  font-weight: 500;
}

.hero-subtitle {
  padding-top: 24px;
  padding-bottom: 40px;
}

/* Контейнер hero */
.hero-container {
  padding: 0 1rem 2rem;
}

/* Основной контент - центрируется на мобильных */
.hero-main-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
  padding-top: 0.5rem;
}

/* Обрезка секции для скрытия половины логотипа */
.hero-section-clip {
  overflow: hidden;
}

/* Логотип - абсолютное позиционирование */
.hero-logo-container {
  position: absolute;

  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  /* Увеличен z-index чтобы быть поверх следующих блоков */
  pointer-events: none;
  width: 100vw;
  overflow: visible;
}

.hero-logo-container .lottie-logo {
  position: absolute;
  left: 50%;
  transform: translateX(-50%) scale(0.5) translateZ(0);
  width: 120vh;
  z-index: 51;
  /* Еще выше чтобы точно быть поверх */
}

.hero-logo-container .loading-text {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 1rem;
}

.hero-logo-container :deep(svg) {
  width: 100% !important;
  height: 100% !important;
}

/* Мобильная версия - выравнивание по верху */
@media (max-width: 1023px) {
  .hero-main-content {
    justify-content: flex-start;
    padding-top: 0;
    margin-top: 180px;
    /* Высота header + отступ */
    margin-bottom: 1rem;
    /* Отступ перед логотипом */
  }

  .hero-logo-container .lottie-logo {
    top: 40vh;
  }
}

/* Десктопная версия */
@media (min-width: 1024px) {
  .hero-container {
    padding: 3rem 2rem 0;
    justify-content: flex-start;
  }

  .hero-main-content {
    flex: 0 0 auto;
    padding-top: 8rem;
    margin-bottom: 3rem;
  }

  .hero-logo-container .lottie-logo {
    top: 5vh;
    /* Опущен на 3vh для десктопа */
    width: 180vh;
  }
}

/* Адаптивность текста */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
    line-height: 1.1;
  }

  .hero-subtitle {
    padding-top: 0;
    padding-bottom: 20px;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }
}
</style>
